import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Availability = () => {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);

  const daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  const dayLabels = {
    'MONDAY': 'Monday',
    'TUESDAY': 'Tuesday',
    'WEDNESDAY': 'Wednesday',
    'THURSDAY': 'Thursday',
    'FRIDAY': 'Friday',
    'SATURDAY': 'Saturday',
    'SUNDAY': 'Sunday'
  };

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await api.get('/admin/availability/weekly');
      console.log('Availability response:', response.data);

      // Convert response to array format for editing
      const scheduleArray = daysOfWeek.map(day => {
        const existing = response.data.schedule?.find(s => s.dayOfWeek === day);
        return existing || {
          dayOfWeek: day,
          startTime: '09:00',
          endTime: '17:00',
          isActive: false
        };
      });

      setAvailability(scheduleArray);
    } catch (err) {
      console.error('Fetch availability error:', err);
      setError(err.response?.data?.error || 'Failed to fetch availability');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (index) => {
    const updated = [...availability];
    updated[index].isActive = !updated[index].isActive;
    setAvailability(updated);
  };

  const handleTimeChange = (index, field, value) => {
    const updated = [...availability];
    updated[index][field] = value;
    setAvailability(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await api.post('/admin/availability/weekly', {
        schedule: availability
      });

      setSuccess('Availability schedule updated successfully!');
      setEditMode(false);

      // Refresh data
      await fetchAvailability();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save availability error:', err);
      setError(err.response?.data?.error || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setError('');
    setSuccess('');
    fetchAvailability(); // Reset to original data
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading availability...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-primary">Weekly Availability</h1>
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Schedule
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-md mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <p className="text-gray-600">
            {editMode
              ? 'Set your weekly availability schedule. Toggle days on/off and set start/end times.'
              : 'View your weekly availability schedule. This shows when customers can book appointments.'
            }
          </p>
        </div>

        <div className="space-y-4">
          {availability.map((day, index) => (
            <div
              key={day.dayOfWeek}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 w-32">
                    {dayLabels[day.dayOfWeek]}
                  </h3>

                  {editMode ? (
                    <div className="flex items-center space-x-4 flex-1">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={day.isActive}
                          onChange={() => handleToggleDay(index)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-gray-700">Available</span>
                      </label>

                      {day.isActive && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="time"
                            value={day.startTime}
                            onChange={(e) => handleTimeChange(index, 'startTime', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="time"
                            value={day.endTime}
                            onChange={(e) => handleTimeChange(index, 'endTime', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {day.isActive ? (
                        <div className="flex items-center space-x-4">
                          <span className="text-gray-600">
                            {day.startTime} - {day.endTime}
                          </span>
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                            Available
                          </span>
                        </div>
                      ) : (
                        <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                          Unavailable
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {editMode && (
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {!editMode && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Click "Edit Schedule" to modify your weekly availability hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Availability;
